import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import isEqual from 'lodash/isEqual';
import { useFeatureFlagEnabled } from 'posthog-js/react';

import { useDebounce } from 'hooks/useDebounce';
import { useDefaultFilters } from 'hooks/useDefaultFilters';
import { useNewFilters } from 'hooks/useFilters';
import { createHandle } from 'services/breadcrumbs/breadcrumbs';
import { useConnectedPartners, useCurrentPartner } from 'services/repositories/partners/partners';
import { useInfiniteTransactions } from 'services/repositories/transactions/transactions';
import { EMPTY_FIELD } from 'support/helpers/const/const';
import { formatDayAndTime } from 'support/helpers/dateTime/dateTime';
import { FEATURE_FLAGS } from 'support/helpers/featureFlags/featureFlags';
import {
  addPartnersToFiltersConfig,
  SEARCH_DEBOUNCE_MS,
  withoutShortSearchTerms,
} from 'support/helpers/filters/filters';
import { routeToPage, routeToTransactionPage } from 'support/helpers/navigation/navigation';
import { translationForDocumentTypeWithVirtual } from 'support/helpers/transactions/transactions';
import { TransactionDTO, TransactionsRoutes } from 'support/types';
import { TransactionFilterKey } from 'support/types/filterKeys';
import { AIBadge } from 'components/Display/AIBadge/AIBadge';
import { Filters } from 'components/Filters/Filters';
import { Button } from 'components/Form/Button';
import { ListTableLongCell } from 'components/ListTable/components/ListTableLongCell/ListTableLongCell';
import { InfiniteScroll } from 'components/ListTable/InfiniteScroll/InfiniteScroll';
import { ListTable } from 'components/ListTable/ListTable';
import { TableHeader, TableItem } from 'components/ListTable/types';
import { Page } from 'components/Page/Page';
import { TransactionStatusDisplay } from 'components/TransactionStatusDisplay/TransactionStatusDisplay';
import { Translate } from 'components/Translate/Translate';

import { useImportableIntegrations } from './hooks/useImportableIntegrations';
import { ImportDocumentsButton } from './components/ImportDocumentsButton/ImportDocumentsButton';
import { RelatedOrders } from './components/RelatedOrders/RelatedOrders';
import { ExportTransactionsButton } from './ExportTransactionsButton';
import { PendingTransactions } from './PendingTransactions/PendingTransactions';
import { getDefaultFilterConfig } from './tableConfig';
import { TransactionsTabs } from './TransactionsTabs/TransactionsTabs';

export function Transactions() {
  const { t } = useTranslation();
  const { data: currentPartner } = useCurrentPartner();
  const { data: connectedPartnersData } = useConnectedPartners();
  const enabled = useFeatureFlagEnabled(FEATURE_FLAGS.RECONCILIATION);

  const filterConfig = useMemo(
    () =>
      addPartnersToFiltersConfig(
        getDefaultFilterConfig(enabled),
        TransactionFilterKey.partnerId,
        connectedPartnersData,
      ),
    [connectedPartnersData, enabled],
  );

  const { defaultFilterValues, saveAsDefault } = useDefaultFilters('transactions');

  const { filters, filterValues, updateFilters, clearAllFilters, perPage } = useNewFilters(
    filterConfig,
    'transactions',
    defaultFilterValues,
  );

  const hasFilters = filterValues && filterValues.length > 0;
  // Same gate as the header button: only when the partner has an active Xentral integration to import from.
  const canImportDocuments = useImportableIntegrations().length > 0;
  const debouncedFilterValues = useDebounce(filterValues, SEARCH_DEBOUNCE_MS);
  const differentFilters = !isEqual(debouncedFilterValues, filterValues);
  const searchFilterValues = useMemo(
    () => withoutShortSearchTerms(debouncedFilterValues ?? [], filters),
    [debouncedFilterValues, filters],
  );

  const {
    data: transactionsData,
    isLoading: transactionsDataLoading,
    isFetchingNextPage,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
  } = useInfiniteTransactions({
    variables: {
      query: {
        filterValues: searchFilterValues,
        perPage,
      },
    },
  });

  // Keep these columns in sync with the CSV export at
  // procuros-mvp/src/Support/Services/MessageExport/TransactionsCsvExporter.php
  // (HEADERS + formatRow) and the locked TransactionExportTest header assertion.
  const tableHeaders: Array<TableHeader> = [
    { label: t('transactions:list.tableHeaders.externalReference') },
    { label: t('transactions:list.tableHeaders.type') },
    { label: t('transactions:list.tableHeaders.relatedOrders') },
    { label: t('transactions:list.tableHeaders.tradePartner') },
    { label: t('transactions:list.tableHeaders.status') },
    { label: t('transactions:list.tableHeaders.flow') },
    { label: t('transactions:list.tableHeaders.date') },
  ];

  const buildRow = (transaction: TransactionDTO): Array<TableItem> => {
    const partner = currentPartner?.id !== transaction.sender?.id ? transaction.sender : transaction.receiver;
    const typeLocale = translationForDocumentTypeWithVirtual(t, transaction.documentType);
    return [
      {
        element: (
          <span className="flex min-w-0 items-center gap-2">
            <ListTableLongCell className="min-w-0">
              {transaction.externalMessageRef?.length ? transaction.externalMessageRef : EMPTY_FIELD}
            </ListTableLongCell>
            {transaction.isUsageBased && <AIBadge />}
          </span>
        ),
        key: `external-ref ${transaction.id ?? ''}`,
        className: 'text-gray-900 font-medium group-hover:text-procuros-green-600 max-w-xs',
      },
      {
        element: transaction.documentType?.length ? typeLocale : '–',
        key: 'type',
      },
      {
        element: <RelatedOrders relatedOrderMessages={transaction.relatedOrderMessages} />,
        className: 'max-w-48 truncate',
        key: 'related-orders',
      },
      {
        element: partner?.name ?? '–',
        key: 'partner-name',
        longText: true,
      },
      {
        element: <TransactionStatusDisplay transaction={transaction} currentPartner={currentPartner} />,
        key: 'message-status',
      },
      {
        element: transaction.flow ? t(`common:flow.${transaction.flow}`) : t('common:flow.unknown'),
        key: 'flow',
      },
      {
        element: <span className="tabular-nums">{formatDayAndTime(transaction.createdAt)}</span>,
        key: 'created-at',
      },
    ];
  };

  const data = transactionsData?.pages?.flatMap((page) => page.items);
  const emptyData = data?.length === 0;

  return (
    <Page>
      <Page.Head
        title={t('common:mainMenu.transactions')}
        description={t('transactions:description')}
        rightContent={<ImportDocumentsButton filterValues={filterValues ?? []} />}
      >
        <div className="w-full">
          <TransactionsTabs selectedIndex={0} />

          <Filters
            propertyFilters={filters}
            onFiltersChange={updateFilters}
            filterValues={filterValues}
            clearAllFilters={clearAllFilters}
            onSaveAsDefault={() => saveAsDefault(filterValues ?? [])}
            rightSlot={<ExportTransactionsButton filterValues={filterValues ?? []} />}
          />
        </div>
      </Page.Head>

      <Page.Section>
        <PendingTransactions />
        <ListTable<TransactionDTO>
          headers={tableHeaders}
          data={data}
          rowBuilder={buildRow}
          rowClickPath={(transaction) => routeToTransactionPage(transaction.internalType, transaction.id)}
          isLoading={transactionsDataLoading || (differentFilters && emptyData)}
          pagination={
            <InfiniteScroll
              hasMore={Boolean(hasNextPage)}
              loadMore={fetchNextPage}
              loading={isFetchingNextPage}
              noMoreItemsEnabled={Boolean(hasPreviousPage)}
            />
          }
          emptyState={
            <ListTable.EmptyState
              background="gray"
              cols={tableHeaders.length}
              footnote={
                <div className="flex flex-col items-center gap-4">
                  {(hasFilters || canImportDocuments) && (
                    <div className="flex flex-wrap items-center justify-center gap-3">
                      {hasFilters && (
                        <Button variant="secondary" onClick={clearAllFilters} analyticsId="transactions:clear_filters">
                          {t('common:table.withFilters.emptyState.cta')}
                        </Button>
                      )}
                      <ImportDocumentsButton filterValues={filterValues ?? []} />
                    </div>
                  )}
                  <Translate i18nKey="transactions:list.emptyStateDocHint" />
                </div>
              }
            >
              {hasFilters ? t('common:table.withFilters.emptyState.title') : t('transactions:list.emptyState')}
            </ListTable.EmptyState>
          }
        />
      </Page.Section>
    </Page>
  );
}

/**
 * i18n
 *
 * t('common:flow.LIVE')
 * t('common:flow.TEST')
 * t('common:flow.IDLE')
 * t('common:flow.unknown')
 */

Transactions.handle = createHandle(() => ({
  label: (t: TFunction) => t('transactions:list.breadcrumb'),
  to: routeToPage(TransactionsRoutes.root),
  key: TransactionsRoutes.root,
}));
